'use client';

import { useState, useEffect } from 'react';
import {
  MessageCircle,
  X,
  Mail,
  Send,
  CheckCircle,
  MessageSquare,
  Clock,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

type Panel = 'channels' | 'form' | 'success';
type Status = 'idle' | 'submitting' | 'success' | 'error';

const config = {
  whatsapp: '8613800138000',
  email: 'info@wfd.jcwuyou.com',
  workingHours: 'Mon-Sat: 9:00 AM - 6:00 PM (GMT+8)',
  whatsappMessage: "Hi! I'm interested in your EV repair solutions. Please contact me.",
};

const channels = [
  {
    id: 'whatsapp',
    icon: MessageCircle,
    name: 'WhatsApp',
    desc: 'Instant chat with our team',
    color: 'from-green-500 to-emerald-500',
    action: () => {
      const url = `https://wa.me/${config.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(config.whatsappMessage)}`;
      window.open(url, '_blank');
    },
  },
  {
    id: 'email',
    icon: Mail,
    name: 'Email',
    desc: 'Detailed inquiry & documents',
    color: 'from-emerald-500 to-teal-500',
    action: () => {
      // Switch to form panel
    },
  },
  {
    id: 'form',
    icon: MessageSquare,
    name: 'Quick Inquiry',
    desc: 'Get a quote within 24h',
    color: 'from-amber-500 to-orange-500',
    action: () => {
      // Switch to form panel
    },
  },
];

export function ContactWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [panel, setPanel] = useState<Panel>('channels');
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('/api/contact/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: formData.get('company') || 'Quick Inquiry',
          cityCountry: formData.get('country') || '',
          contactPerson: formData.get('name'),
          email: formData.get('email'),
          whatsapp: formData.get('whatsapp') || '',
          interestedIn: 'Quick Inquiry (Widget)',
          requirements: formData.get('message') || '',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Submission failed');
      }

      setStatus('success');
      setPanel('success');
    } catch (error) {
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Something went wrong');
    }
  };

  const handleChannelClick = (channelId: string) => {
    const channel = channels.find((c) => c.id === channelId);
    if (!channel) return;

    if (channelId === 'email' || channelId === 'form') {
      setPanel('form');
    } else {
      channel.action();
    }
  };

  const closeWidget = () => {
    setIsOpen(false);
    setTimeout(() => {
      setPanel('channels');
      setStatus('idle');
    }, 300);
  };

  return (
    <div
      className={`fixed bottom-6 left-6 z-[60] transition-all duration-500 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      {/* Expanded Panel */}
      {isOpen && (
        <div className="absolute bottom-20 left-0 w-[340px] sm:w-[360px] rounded-2xl bg-[oklch(0.20_0.015_220)] border border-[oklch(0.28_0.025_170)] shadow-2xl shadow-black/40 shadow-[0_0_40px_oklch(0.62_0.19_150)_/_0.15] overflow-hidden animate-[fadeInUp_0.3s_ease-out]">
          {/* Header */}
          <div className="px-5 py-4 bg-gradient-to-r from-[oklch(0.62_0.19_150)]/20 to-[oklch(0.85_0.12_140)]/10 border-b border-[oklch(0.28_0.025_170)] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-[oklch(0.62_0.19_150)]/20 flex items-center justify-center">
                {panel === 'channels' && <MessageCircle className="w-5 h-5 text-[oklch(0.72_0.22_150)]" />}
                {panel === 'form' && <MessageSquare className="w-5 h-5 text-[oklch(0.72_0.22_150)]" />}
                {panel === 'success' && <CheckCircle className="w-5 h-5 text-[oklch(0.72_0.22_150)]" />}
              </div>
              <div>
                <h3 className="font-semibold text-foreground text-sm">
                  {panel === 'channels' && 'Contact Us'}
                  {panel === 'form' && 'Quick Inquiry'}
                  {panel === 'success' && 'Thank You!'}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {panel === 'channels' && 'Choose your preferred way'}
                  {panel === 'form' && 'We reply within 24 hours'}
                  {panel === 'success' && 'Our team will contact you soon'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {panel !== 'channels' && (
                <button
                  onClick={() => setPanel('channels')}
                  className="p-1.5 rounded-lg hover:bg-[oklch(0.26_0.02_220)] text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Back"
                >
                  <ChevronRight className="w-4 h-4 rotate-180" />
                </button>
              )}
              <button
                onClick={closeWidget}
                className="p-1.5 rounded-lg hover:bg-[oklch(0.26_0.02_220)] text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-4 max-h-[65vh] overflow-y-auto">
            {panel === 'channels' && (
              <div className="space-y-2">
                {channels.map((channel) => (
                  <button
                    key={channel.id}
                    onClick={() => handleChannelClick(channel.id)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl bg-[oklch(0.26_0.02_220)]/50 border border-[oklch(0.28_0.025_170)] hover:border-[oklch(0.62_0.19_150)]/50 hover:bg-[oklch(0.28_0.025_220)] transition-all group"
                  >
                    <div
                      className={`w-10 h-10 rounded-xl bg-gradient-to-br ${channel.color} flex items-center justify-center flex-shrink-0 shadow-lg`}
                    >
                      <channel.icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-medium text-foreground text-sm">{channel.name}</div>
                      <div className="text-xs text-muted-foreground">{channel.desc}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-[oklch(0.72_0.22_150)] group-hover:translate-x-0.5 transition-all" />
                  </button>
                ))}

                {/* Working Hours */}
                <div className="mt-4 pt-4 border-t border-[oklch(0.28_0.025_170)]">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    <span>{config.workingHours}</span>
                  </div>
                </div>
              </div>
            )}

            {panel === 'form' && (
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="widget-name" className="text-xs text-muted-foreground">
                      Name *
                    </Label>
                    <Input
                      id="widget-name"
                      name="name"
                      required
                      size={1}
                      className="mt-1 h-9 text-sm bg-[oklch(0.15_0.01_220)] border-[oklch(0.28_0.025_170)] focus:border-[oklch(0.62_0.19_150)]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="widget-email" className="text-xs text-muted-foreground">
                      Email *
                    </Label>
                    <Input
                      id="widget-email"
                      name="email"
                      type="email"
                      required
                      size={1}
                      className="mt-1 h-9 text-sm bg-[oklch(0.15_0.01_220)] border-[oklch(0.28_0.025_170)] focus:border-[oklch(0.62_0.19_150)]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="widget-company" className="text-xs text-muted-foreground">
                      Company
                    </Label>
                    <Input
                      id="widget-company"
                      name="company"
                      size={1}
                      className="mt-1 h-9 text-sm bg-[oklch(0.15_0.01_220)] border-[oklch(0.28_0.025_170)] focus:border-[oklch(0.62_0.19_150)]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="widget-country" className="text-xs text-muted-foreground">
                      Country
                    </Label>
                    <Input
                      id="widget-country"
                      name="country"
                      size={1}
                      className="mt-1 h-9 text-sm bg-[oklch(0.15_0.01_220)] border-[oklch(0.28_0.025_170)] focus:border-[oklch(0.62_0.19_150)]"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="widget-message" className="text-xs text-muted-foreground">
                    Your Requirements
                  </Label>
                  <Textarea
                    id="widget-message"
                    name="message"
                    rows={3}
                    placeholder="Tell us about your needs..."
                    className="mt-1 text-sm bg-[oklch(0.15_0.01_220)] border-[oklch(0.28_0.025_170)] focus:border-[oklch(0.62_0.19_150)] resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full h-10 text-sm font-medium bg-gradient-to-r from-[oklch(0.62_0.19_150)] to-[oklch(0.85_0.12_140)] text-[oklch(0.15_0.01_220)] hover:from-[oklch(0.72_0.22_150)] hover:to-[oklch(0.85_0.12_140)] shadow-lg shadow-[oklch(0.62_0.19_150)]/25"
                >
                  {status === 'submitting' ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      Send Inquiry
                    </span>
                  )}
                </Button>

                {status === 'error' && (
                  <p className="text-xs text-red-400 text-center">{errorMessage}</p>
                )}
              </form>
            )}

            {panel === 'success' && (
              <div className="text-center py-6">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-green-500/10 flex items-center justify-center">
                  <CheckCircle className="w-7 h-7 text-[oklch(0.72_0.22_150)]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-foreground">Message Sent!</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Thank you for reaching out. Our EV solution specialist will contact you within 24 hours.
                </p>
                <Button
                  onClick={closeWidget}
                  variant="outline"
                  size="sm"
                  className="border-[oklch(0.28_0.025_170)] text-foreground hover:bg-[oklch(0.26_0.02_220)]"
                >
                  Close
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative w-14 h-14 rounded-full bg-gradient-to-br from-[oklch(0.62_0.19_150)] to-[oklch(0.85_0.12_140)] shadow-lg shadow-[oklch(0.62_0.19_150)]/40 hover:shadow-xl hover:shadow-[oklch(0.62_0.19_150)]/50 hover:scale-110 transition-all duration-300 flex items-center justify-center group ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        aria-label="Contact us"
      >
        {/* Pulse ring */}
        <span className="absolute inset-0 rounded-full bg-[oklch(0.62_0.19_150)]/30 animate-ping" />
        <span className="absolute -inset-1 rounded-full bg-[oklch(0.62_0.19_150)]/15 animate-pulse" />
        
        {isOpen ? (
          <X className="w-6 h-6 text-[oklch(0.15_0.01_220)] relative z-10" />
        ) : (
          <MessageCircle className="w-6 h-6 text-[oklch(0.15_0.01_220)] relative z-10" />
        )}
      </button>
    </div>
  );
}
